import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom'; // <-- import useParams
import { FaStar, FaRegStar, FaFilter, FaChevronDown } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { RatingContext } from '../../context/RatingContext';
import { UPLOAD_URL } from '../../config/apiConfig';

const StarRating = ({ rating, totalStars = 5 }) => {
  const fullStars = Math.floor(rating);
  return (
    <div className="flex items-center gap-1">
      {[...Array(totalStars)].map((_, index) => (
        <span key={index} className="text-yellow-400">
          {index < fullStars ? <FaStar size={12} /> : <FaRegStar size={12} />}
        </span>
      ))}
    </div>
  );
};

const ReviewItem = ({ user_name, stars, create_audit_time, review, image, index }) => (
  <motion.div
    className="mt-6"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
  >
    <div className="flex flex-wrap gap-5 justify-between w-full max-md:flex-col max-md:gap-3">
      <div className="flex gap-4 font-semibold">
        <img
          src={`${UPLOAD_URL}/users/${image}` }
          alt={`${user_name}'s profile`}
          className="object-contain shrink-0 rounded-full aspect-square w-[50px] h-[50px]"
        />
        <div className="flex flex-col justify-center">
          <h4 className="text-base tracking-normal text-sky-950 mb-1">{user_name}</h4>
          <div className="flex items-center gap-2">
            <StarRating rating={parseFloat(stars)} />
            <span className="text-xs text-sky-950">{stars}</span>
          </div>
        </div>
      </div>
      <time
        className="self-start text-sm font-medium text-zinc-400 max-md:self-auto"
        dateTime={create_audit_time}
      >
        {new Date(create_audit_time).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </time>
    </div>
    <p className="mt-3 text-sm leading-6 text-gray-800">{review}</p>
    <hr className="mt-6 w-full h-px border border-gray-200" />
  </motion.div>
);

const ReviewsSection = () => {
  const { id } = useParams(); // <-- get product id from URL
  const [filterVisible, setFilterVisible] = useState(false);
  const [sortOrder, setSortOrder] = useState('Newest');
  const [ratingFilter, setRatingFilter] = useState('All');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const { getReviews } = useContext(RatingContext);

  // Fetch reviews dynamically
  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      try {
        const data = await getReviews(id); // use id from URL
        setReviews(data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchReviews();
  }, [id, getReviews]);

  // Apply rating filter
  const filteredReviews =
    ratingFilter === '5'
      ? reviews.filter((r) => Math.floor(r.stars) === 5)
      : reviews;

  // Sort reviews by date
  const sortedReviews = [...filteredReviews].sort((a, b) => {
    const dateA = new Date(a.create_audit_time);
    const dateB = new Date(b.create_audit_time);
    return sortOrder === 'Newest' ? dateB - dateA : dateA - dateB;
  });

  const handleRatingSelect = (option) => {
    setRatingFilter(option);
    setFilterVisible(false);
  };

  const handleDateSortSelect = (option) => {
    setSortOrder(option);
    setFilterVisible(false);
  };

  return (
    <motion.section
      className="mt-8 relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.8 }}
    >
      <hr className="w-full h-px border border-gray-200" />

      <div className="flex flex-wrap gap-6 justify-between items-center mt-6 max-md:flex-col max-md:items-start max-md:gap-4">
        <h3 className="text-xl font-semibold text-sky-950">Reviews</h3>
        <motion.button
          className="flex items-center gap-2 px-4 py-2 text-red-500 font-medium hover:bg-red-50 rounded-lg transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setFilterVisible(!filterVisible)}
        >
          <FaFilter size={14} />
          <span>Filter</span>
          <FaChevronDown
            size={12}
            className={`transition-transform ${filterVisible ? 'rotate-180' : ''}`}
          />
        </motion.button>
      </div>

      {/* Filter dropdown */}
      <AnimatePresence>
        {filterVisible && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg w-56 p-3 z-10"
          >
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Ratings</h4>
            {['All', '5'].map((option) => (
              <button
                key={option}
                onClick={() => handleRatingSelect(option)}
                className={`block w-full text-left px-3 py-1.5 rounded-md text-sm ${
                  ratingFilter === option
                    ? 'bg-red-100 text-red-500'
                    : 'hover:bg-gray-100'
                }`}
              >
                {option === 'All' ? 'All Ratings' : '5 Stars Only'}
              </button>
            ))}

            <h4 className="text-sm font-semibold text-gray-700 mt-3 mb-2">Sort by Date</h4>
            {['Newest', 'Oldest'].map((option) => (
              <button
                key={option}
                onClick={() => handleDateSortSelect(option)}
                className={`block w-full text-left px-3 py-1.5 rounded-md text-sm ${
                  sortOrder === option
                    ? 'bg-red-100 text-red-500'
                    : 'hover:bg-gray-100'
                }`}
              >
                {option}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-2 mt-4">
        {loading ? (
          <p className="text-gray-500">Loading reviews...</p>
        ) : sortedReviews.length > 0 ? (
          sortedReviews.map((review, index) => (
            <ReviewItem key={review.id} {...review} index={index} />
          ))
        ) : (
          <p className="text-gray-500 mt-4">No reviews found.</p>
        )}
      </div>
    </motion.section>
  );
};

export default ReviewsSection;
